import {
  AiBuilderFlowPartialUpdate,
  AiBuilderNarration,
  AiBuilderProgress,
  WebsocketClientEvent,
} from '@activepieces/shared';
import { t } from 'i18next';
import { MutableRefObject, useEffect } from 'react';
import { Socket } from 'socket.io-client';

import { BuilderState } from '../builder-hooks';

import { AiBuilderChatMessage, isStaleTurnEvent } from './ai-builder-utils';

type UseAiBuilderTurnEventsParams = {
  socket: Socket | null | undefined;
  flowId: string;
  /** The turn this page last dispatched — read at event time, not a dependency: a new turn must not tear down and re-subscribe the listeners */
  requestIdRef: MutableRefObject<string | undefined>;
  setLoadingMessage: (message: string) => void;
  applyAiPartialFlowElements: BuilderState['applyAiPartialFlowElements'];
  triggerCanvasRefit: BuilderState['triggerCanvasRefit'];
  addMessage: (flowId: string, message: AiBuilderChatMessage) => void;
};

/**
 * The three socket events a turn in flight can emit on its own, outside the
 * request/response pair (`generateFlow`): a progress label, a live partial
 * canvas preview, and a narration line. One socket per tab, hence the flowId
 * filter — a turn still running for another flow keeps emitting after the
 * tab moved to this flow's chat. The requestId filter is a second, narrower
 * one: a previous turn on THIS SAME flow (an abandoned retry, another tab)
 * must not have its late line land in the one now running.
 */
export const useAiBuilderTurnEvents = (
  params: UseAiBuilderTurnEventsParams,
): void => {
  const {
    socket,
    flowId,
    requestIdRef,
    setLoadingMessage,
    applyAiPartialFlowElements,
    triggerCanvasRefit,
    addMessage,
  } = params;

  useEffect(() => {
    if (!socket) return;

    const handleAiBuilderProgress = (data: AiBuilderProgress) => {
      if (data.flowId !== flowId) return;
      if (isStaleTurnEvent(data.requestId, requestIdRef.current)) return;
      setLoadingMessage(t(data.message || 'Thinking...'));
    };

    const handlePartialUpdate = (data: AiBuilderFlowPartialUpdate) => {
      if (data.flowId !== flowId) return;
      if (isStaleTurnEvent(data.requestId, requestIdRef.current)) return;
      // Local preview only: the agent persists server-side. No auto-layout
      // here: it persists a DRAG_STEP per node, and those steps do not exist
      // server-side yet mid-turn (the request 500s and halts the save queue).
      // Layout runs on the final response instead.
      if (data.flowElements) {
        applyAiPartialFlowElements(data.flowElements);
        triggerCanvasRefit();
      }
    };

    // A mid-turn line the assistant reports on its own, persisted to its own
    // flow's chat, shown or not. `requestIdRef` only tracks the turn on the
    // mounted flow, so the staleness check applies to that flow alone: a turn
    // still running on another flow keeps its narration.
    const handleNarration = (payload: AiBuilderNarration) => {
      if (
        payload.flowId === flowId &&
        isStaleTurnEvent(payload.requestId, requestIdRef.current)
      ) {
        return;
      }
      addMessage(payload.flowId, {
        body: payload.line,
        userType: 'bot',
        narration: true,
      });
    };

    socket.on(
      WebsocketClientEvent.AI_BUILDER_PROGRESS,
      handleAiBuilderProgress,
    );
    socket.on(
      WebsocketClientEvent.AI_BUILDER_FLOW_PARTIAL_UPDATE,
      handlePartialUpdate,
    );
    socket.on(WebsocketClientEvent.AI_BUILDER_NARRATION, handleNarration);
    return () => {
      socket.off(
        WebsocketClientEvent.AI_BUILDER_PROGRESS,
        handleAiBuilderProgress,
      );
      socket.off(
        WebsocketClientEvent.AI_BUILDER_FLOW_PARTIAL_UPDATE,
        handlePartialUpdate,
      );
      socket.off(WebsocketClientEvent.AI_BUILDER_NARRATION, handleNarration);
    };
  }, [
    socket,
    flowId,
    requestIdRef,
    setLoadingMessage,
    applyAiPartialFlowElements,
    triggerCanvasRefit,
    addMessage,
  ]);
};
